import React, { useEffect, useRef } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  PolicyPageWrapper,
  PolicyHeroSection,
  PolicyContainer,
  AboutSection,
  ValueCard,
  ValuesGrid,
  PolicyHighlight,
  CoreValueItem,
} from "./styles";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Policy = () => {
  const heroRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const valuesGridRef = useRef<HTMLDivElement>(null);
  const policyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;

    if (!hero) return;

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo(
        hero.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      gsap.fromTo(
        hero.querySelector("h1"),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );

      gsap.fromTo(
        hero.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.4 }
      );

      gsap.fromTo(
        hero.querySelectorAll(".badge"),
        { opacity: 0, y: 20, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "back.out(1.7)",
          delay: 0.5,
        }
      );

      // Floating shapes
      gsap.fromTo(
        hero.querySelectorAll(".floatingShape"),
        { opacity: 0, scale: 0 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.7)",
          delay: 0.3,
        }
      );
    }, hero);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const about = aboutRef.current;
    const valuesGrid = valuesGridRef.current;
    const policy = policyRef.current;

    if (!container || !about || !valuesGrid || !policy) return;

    const ctx = gsap.context(() => {
      // About section animation
      gsap.fromTo(
        about.querySelector(".sectionLabel"),
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: about,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        about.querySelector("h2"),
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: about,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        about.querySelector(".leadParagraph"),
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: about,
            start: "top 75%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        about.querySelector(".contentParagraph"),
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: about,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Value cards staggered animation
      const cards = valuesGrid.querySelectorAll(".value-card");
      gsap.fromTo(
        cards,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: valuesGrid,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Core values animation
      const coreValues = valuesGrid.querySelectorAll(".core-value-item");
      gsap.fromTo(
        coreValues,
        { opacity: 0, y: 30, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: coreValues[0],
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Policy highlight animation
      gsap.fromTo(
        policy,
        { opacity: 0, y: 40, scale: 0.98 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: policy,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, container);

    return () => ctx.revert();
  }, []);

  const coreValues = [
    {
      title: "Integrity",
      description: "Upholding professional integrity and fair play",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      title: "Excellence",
      description: "Exceeding stakeholder expectations consistently",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
    {
      title: "Partnership",
      description: "Valuing strategic partnerships with customers",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      title: "Expertise",
      description: "Certified engineers ensuring quality service",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
    },
  ];

  return (
    <PolicyPageWrapper>
      <Header />

      <PolicyHeroSection ref={heroRef}>
        {/* Background decorations */}
        <div className="heroBg">
          <div className="gradientOrb orb1" />
          <div className="gradientOrb orb2" />
          <div className="gridPattern" />
        </div>

        {/* Floating shapes */}
        <div className="floatingShape shape1" />
        <div className="floatingShape shape2" />
        <div className="floatingShape shape3" />

        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>About Us</span>
            <span className="line" />
          </div>

          <h1>
            Sahara <span className="highlight">Computers</span>
          </h1>

          <p className="heroDescription">
            Your trusted technology partner in Kuwait for over 25 years,
            delivering excellence in IT solutions, hardware, and services.
          </p>

          <div className="heroBadges">
            <div className="badge">
              <div className="badgeIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div className="badgeText">
                <div className="badgeNumber">25+</div>
                <div className="badgeLabel">Years Experience</div>
              </div>
            </div>
            <div className="badge">
              <div className="badgeIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="badgeText">
                <div className="badgeNumber">HP</div>
                <div className="badgeLabel">Authorized Partner</div>
              </div>
            </div>
            <div className="badge">
              <div className="badgeIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="badgeText">
                <div className="badgeNumber">500+</div>
                <div className="badgeLabel">Happy Clients</div>
              </div>
            </div>
          </div>
        </div>
      </PolicyHeroSection>

      <PolicyContainer ref={containerRef}>
        <AboutSection ref={aboutRef}>
          <div className="aboutLeft">
            <div className="sectionLabel">
              <span className="line" />
              <span>Our Story</span>
            </div>
            <h2>
              Leading IT
              <br />
              <span className="highlight">Solutions</span>
              <br />
              Provider
            </h2>
          </div>

          <div className="aboutRight">
            <p className="leadParagraph">
              We are a leading supplier in the Kuwait IT market, specializing in
              IT hardware, software, and consumables for over 25 years. As an
              authorized HP Power & HP Power service Partner (MPS), we assure
              the authenticity of all HP products we supply.
            </p>
            <p className="contentParagraph">
              Established in 2000, Sahara is a service-oriented organization
              that proudly represents leading global brands in IT hardware,
              software, networks, and office equipment. With a focus on
              transforming new technologies into reliable enterprise solutions,
              we have earned a diverse clientele, ranging from home users and
              small to medium-sized businesses to large corporations, banks,
              hospitals, universities, schools, and government institutions.
            </p>
          </div>
        </AboutSection>

        <ValuesGrid ref={valuesGridRef}>
          <ValueCard className="value-card">
            <div className="cardHeader">
              <div className="icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <h4>Our Vision</h4>
            </div>
            <p>
              To provide excellent computer hardware sales, support, and
              networking solutions to government and private companies in Kuwait
              at an affordable price within the shortest time.
            </p>
          </ValueCard>

          <ValueCard className="value-card">
            <div className="cardHeader">
              <div className="icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h4>Our Mission</h4>
            </div>
            <p>
              We strive for 100% customer satisfaction in both sales and
              services. Our goal is to ensure a trouble-free work environment
              for systems under our care.
            </p>
          </ValueCard>

          <ValueCard className="value-card full-width">
            <h4>Core Values</h4>
            <div className="core-values-grid">
              {coreValues.map((value, index) => (
                <CoreValueItem key={index} className="core-value-item">
                  <div className="valueIcon">{value.icon}</div>
                  <span className="value-title">{value.title}</span>
                  <span className="value-desc">{value.description}</span>
                </CoreValueItem>
              ))}
            </div>
          </ValueCard>
        </ValuesGrid>

        <PolicyHighlight ref={policyRef}>
          <div className="policyContent">
            <div className="badge">Our Policy</div>
            <h4>No solution, no charge.</h4>
            <p>
              If we are unable to resolve your computer problems, there will be
              no charge for our services. However, certain cases may require
              upgrades or replacements, and in such instances, charges may
              apply. Our onsite services are warranted for 30 days.
            </p>
            <a href="/#contactUs" className="ctaButton">
              Get Support Now
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </PolicyHighlight>
      </PolicyContainer>

      <Footer />
    </PolicyPageWrapper>
  );
};

export default Policy;
